import { TRBStatusTableRow, TRBMaxRiskTableRow } from 'src/features/types/trbTypes';

export type SortOrder = 'asc' | 'desc';

/**
 * Formats the year-bearing cptUTC ("MMM d yyyy, HH:mm UTC") for display by
 * stripping the year, so the UI shows "MMM d, HH:mm UTC" (e.g. "Aug 14, 10:30 UTC").
 * Falls back to the original string if it doesn't match the expected shape.
 */
export function formatCptUTCForDisplay(cptUTC: string): string {
    const match = /^([A-Za-z]{3}\s+\d{1,2})\s+\d{4}(,\s+\d{1,2}:\d{2}\s+UTC)$/.exec(cptUTC);
    return match ? `${match[1]}${match[2]}` : cptUTC;
}

/**
 * Computes timeToCPT string and timeToCPTSeconds from a cptUTC string.
 * The cptUTC format is "MMM d yyyy, HH:mm UTC" (e.g. "Aug 14 2026, 10:30 UTC").
 * The backend includes the year so no year inference is needed.
 * Returns { timeToCPT: "Xd Xh Xm", timeToCPTSeconds: number }
 */
export function computeTimeToCPT(cptUTC: string): { timeToCPT: string; timeToCPTSeconds: number } {
    try {
        // Parse "MMM d yyyy, HH:mm" explicitly. Passing a non-ISO string to `new Date()`
        // is implementation-dependent per the ECMAScript spec, so parse the components and
        // build the timestamp with Date.UTC for deterministic, cross-engine behavior.
        const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const cleanedCpt = cptUTC.replace(' UTC', '');
        const match = /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4}),\s+(\d{1,2}):(\d{2})$/.exec(cleanedCpt);
        if (!match) {
            return { timeToCPT: '0m', timeToCPTSeconds: 0 };
        }
        const [, monthStr, dayStr, yearStr, hourStr, minuteStr] = match;
        const month = MONTHS.indexOf(monthStr);
        if (month < 0) {
            return { timeToCPT: '0m', timeToCPTSeconds: 0 };
        }

        const cptDate = new Date(Date.UTC(Number(yearStr), month, Number(dayStr), Number(hourStr), Number(minuteStr)));
        if (isNaN(cptDate.getTime())) {
            return { timeToCPT: '0m', timeToCPTSeconds: 0 };
        }

        const nowMs = Date.now();
        const diffMs = cptDate.getTime() - nowMs;
        const diffSeconds = Math.floor(diffMs / 1000);

        if (diffSeconds <= 0) {
            return { timeToCPT: '0m', timeToCPTSeconds: diffSeconds };
        }

        const totalMinutes = Math.floor(diffSeconds / 60);
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const minutes = totalMinutes % 60;

        let timeToCPT = '';
        if (days !== 0) timeToCPT += `${days}d `;
        if (hours !== 0) timeToCPT += `${hours}h `;
        if (minutes !== 0 || timeToCPT.length === 0) timeToCPT += `${minutes}m`;

        return { timeToCPT: timeToCPT.trim(), timeToCPTSeconds: diffSeconds };
    } catch {
        return { timeToCPT: '0m', timeToCPTSeconds: 0 };
    }
}

export function riskComparison(a: string, b: string, order: SortOrder): number {
    if (a === b) return 0;

    let riskAFloat = parseFloat(a);
    let riskBFloat = parseFloat(b);

    if (isNaN(riskAFloat)) riskAFloat = Number.MAX_VALUE;
    if (isNaN(riskBFloat)) riskBFloat = Number.MAX_VALUE;

    let rank = riskAFloat - riskBFloat;
    if (Math.abs(rank) < 0.0001) return 0;

    return order !== 'desc' ? rank : rank * -1.0;
}

export function timeToCPTComparison(a: number, b: number, order: SortOrder): number {
    const rank = a - b;
    return order !== 'desc' ? rank : rank * -1.0;
}

export function highThresholdMinusRiskComparison(a: string, b: string, order: SortOrder): number {
    if (a === b) return 0;

    const aFloat = parseFloat(a);
    const bFloat = parseFloat(b);
    const aIsNaN = isNaN(aFloat);
    const bIsNaN = isNaN(bFloat);

    // Both non-numeric (and unequal per the check above): treat as equal. Guards against
    // MAX_VALUE - MAX_VALUE being fine but keeps intent explicit and avoids NaN edge cases.
    if (aIsNaN && bIsNaN) return 0;

    // Non-numeric values sort to the bottom (ascending), matching riskComparison.
    const differenceAFloat = aIsNaN ? Number.MAX_VALUE : aFloat;
    const differenceBFloat = bIsNaN ? Number.MAX_VALUE : bFloat;

    const rank = differenceAFloat - differenceBFloat;
    if (Math.abs(rank) < 0.0001) return 0;

    return order !== 'desc' ? rank : rank * -1.0;
}

export type SortField = 'fc' | 'cptUTC' | 'cpt' | 'timeToCPT' | 'lowThreshold' | 'highThreshold' | 'highThresholdMinusRisk' | 'risk' | 'processPathGroup';

export function sortStatusTableData(
    data: TRBStatusTableRow[],
    sortField: SortField,
    sortOrder: SortOrder,
): TRBStatusTableRow[] {
    return [...data].sort((a, b) => {
        switch (sortField) {
            case 'risk':
                return riskComparison(a.risk, b.risk, sortOrder);
            case 'lowThreshold':
                return riskComparison(a.lowThreshold, b.lowThreshold, sortOrder);
            case 'highThreshold':
                return riskComparison(a.highThreshold, b.highThreshold, sortOrder);
            case 'timeToCPT':
            case 'cpt':
            case 'cptUTC':
                // CPT columns are formatted date strings; sort chronologically using the
                // numeric timeToCPTSeconds derived from the same CPT, not lexically.
                return timeToCPTComparison(a.timeToCPTSeconds, b.timeToCPTSeconds, sortOrder);
            default: {
                const valA = String((a as unknown as Record<string, unknown>)[sortField] ?? '');
                const valB = String((b as unknown as Record<string, unknown>)[sortField] ?? '');
                const rank = valA.localeCompare(valB);
                return sortOrder !== 'desc' ? rank : rank * -1;
            }
        }
    });
}

export function sortMaxRiskTableData(
    data: TRBMaxRiskTableRow[],
    sortField: SortField,
    sortOrder: SortOrder,
): TRBMaxRiskTableRow[] {
    return [...data].sort((a, b) => {
        switch (sortField) {
            case 'risk':
                return riskComparison(a.risk, b.risk, sortOrder);
            case 'lowThreshold':
                return riskComparison(a.lowThreshold, b.lowThreshold, sortOrder);
            case 'highThreshold':
                return riskComparison(a.highThreshold, b.highThreshold, sortOrder);
            case 'highThresholdMinusRisk':
                return highThresholdMinusRiskComparison(a.highThresholdMinusRisk, b.highThresholdMinusRisk, sortOrder);
            case 'timeToCPT':
            case 'cpt':
            case 'cptUTC':
                // CPT columns are formatted date strings; sort chronologically using the
                // numeric timeToCPTSeconds derived from the same CPT, not lexically.
                return timeToCPTComparison(a.timeToCPTSeconds, b.timeToCPTSeconds, sortOrder);
            default: {
                const valA = String((a as unknown as Record<string, unknown>)[sortField] ?? '');
                const valB = String((b as unknown as Record<string, unknown>)[sortField] ?? '');
                const rank = valA.localeCompare(valB);
                return sortOrder !== 'desc' ? rank : rank * -1;
            }
        }
    });
}
